import { Box, Stack } from '@mui/system'
import { FC, useCallback, useMemo, useState } from 'react'
import ActivitiesIcon from '../../../../assets/images/specialist-activity-icon.svg?react'
import ExpandAll from '../../../../assets/images/expand-all.svg?react'
import CollapseAll from '../../../../assets/images/collapse-all.svg?react'
import { Typography } from '@mui/material'
import { ActivityCard } from './ActivityCard/ActivityCard'
import { OutdoorEngagement, ProcessedActivity } from '../../../../api/models'

interface SpecialistActivitiesProps {
  engagement: OutdoorEngagement
  processedActivities: ProcessedActivity[]
  scrubberCurrentTimeInSeconds: number
  isPlaying?: boolean
  onSeek: (timeInSeconds: number) => void
}

export const SpecialistActivities: FC<SpecialistActivitiesProps> = ({
  engagement,
  processedActivities,
  scrubberCurrentTimeInSeconds,
  isPlaying = false,
  onSeek
}) => {
  const [shouldExpandAll, setShouldExpandAll] = useState<boolean>(false)
  const [expandCollapseVersion, setExpandCollapseVersion] = useState<number>(0)
  const [cardCollapsedStates, setCardCollapsedStates] = useState<Record<number, boolean>>({})

  const anyCardOpen = Object.values(cardCollapsedStates).some(isCollapsed => !isCollapsed)

  const collapseOrExpandAllActivities = () => {
    setShouldExpandAll(!anyCardOpen)
    setExpandCollapseVersion(prev => prev + 1)
  }

  const handleCardCollapsedChange = useCallback((index: number, isCollapsed: boolean) => {
    setCardCollapsedStates(prev => ({ ...prev, [index]: isCollapsed }))
  }, [])

  const activityTypeTotals = useMemo(
    () =>
      processedActivities.reduce<Record<string, number>>((acc, activity) => {
        acc[activity.type] = (acc[activity.type] ?? 0) + 1
        return acc
      }, {}),
    [processedActivities]
  )

  return (
    <Stack direction="column" data-testid={`engagement-specialist-activities`}>
      <Stack
        direction="row"
        sx={{
          gap: 'var(--spacing-2xs)',
          borderTop: '0.063rem solid var(--border-medium)',
          borderBottom: '0.063rem solid var(--border-medium)',
          alignItems: 'center',
          padding: 'var(--spacing-s)'
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 'var(--spacing-s)', flex: 1 }}>
          <ActivitiesIcon />
          <Typography sx={{ fontWeight: '700' }}>Specialist Activities</Typography>
        </Stack>
        <Box
          onClick={collapseOrExpandAllActivities}
          sx={{ alignItems: 'center', display: 'flex', cursor: 'pointer' }}
          data-testid={`specialist-activities-expand-collapse-all`}
        >
          {anyCardOpen ? <CollapseAll /> : <ExpandAll />}
        </Box>
      </Stack>
      <Stack direction="column">
        {(() => {
          const seenCounts: Record<string, number> = {}
          return processedActivities.map((processedActivity, index) => {
            const type = processedActivity.type
            const activityTypeIndex = (seenCounts[type] ?? 0) + 1
            seenCounts[type] = activityTypeIndex
            return (
              <ActivityCard
                key={index}
                engagement={engagement}
                processedActivity={processedActivity}
                currentTimeInSeconds={scrubberCurrentTimeInSeconds}
                isPlaying={isPlaying}
                shouldExpandAll={shouldExpandAll}
                expandCollapseVersion={expandCollapseVersion}
                cardIndex={index}
                activityTypeIndex={activityTypeIndex}
                activityTypeTotal={activityTypeTotals[type] ?? 1}
                onCollapsedChange={handleCardCollapsedChange}
                onSeek={onSeek}
              />
            )
          })
        })()}
      </Stack>
    </Stack>
  )
}
